import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './Pages.css';
import usePageMeta, {breadcrumbJsonLd} from '../seo/usePageMeta';

const sectionMeta={
  herzwoche:{
    title:'Herzwoche',
    description:'Eindrücke und Aktionen aus der Herzwoche.'
  },
  'unternehmerinnen-messe':{
    title:'Unternehmerinnen Messe',
    description:'Fotos vom Messeauftritt und den Begegnungen vor Ort.'
  },
  'messe-essen':{
    title:'Messe Essen',
    description:'Bilder von der Altenpflege-Messe in Essen.'
  },
  herbstfest:{
    title:'Herbstfest',
    description:'Kaffee, selbstgebackener Kuchen und gemeinsame Stunden bei unserem Herbstfest.'
  },
  'erste-hilfe-training':{
    title:'Erste-Hilfe-Training',
    description:'Unser Team frischt Herz-Lungen-Wiederbelebung und den Umgang mit dem Defibrillator auf.'
  },
  'eitorfer-frühling':{
    title:'Eitorfer Frühling',
    description:'Bilder von unserem Stand bei dem Eitorfer Frühling.'
  }
};

const toDisplayTitle=(folderName)=>{
  return folderName
    .split('-')
    .filter(Boolean)
    .map((part)=>part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const imageContext=require.context('../assets/galerie', true, /\.(png|jpe?g|webp)$/i);

const gallerySections=Object.entries(
  imageContext.keys().reduce((sections, assetPath)=>{
    const normalizedPath=assetPath.replace('./', '');
    const parts=normalizedPath.split('/');
    const folderName=parts.length > 1 ? parts[0] : 'allgemein';
    const fileName=parts[parts.length - 1];
    const sectionTitle=sectionMeta[folderName]?.title || toDisplayTitle(folderName);

    if(!sections[folderName]){
      sections[folderName]=[];
    }

    sections[folderName].push({
      id:`${folderName}-${fileName.replace(/\.[^.]+$/, '')}`,
      src:imageContext(assetPath),
      alt:`${sectionTitle} ${sections[folderName].length + 1}`
    });

    return sections;
  }, {})
)
  .sort(([left], [right])=>{
    if(left === 'herzwoche') return -1;
    if(right === 'herzwoche') return 1;
    return left.localeCompare(right, 'de');
  })
  .map(([folderName, images])=>({
    id:folderName,
    title:sectionMeta[folderName]?.title || toDisplayTitle(folderName),
    description:sectionMeta[folderName]?.description || 'Bilder aus diesem Bereich.',
    images
  }));

const GallerySection=({section})=>{
  const [activeIndex, setActiveIndex]=useState(0);
  const totalImages=section.images.length;
  const activeImage=section.images[activeIndex];
  const prevImage=section.images[(activeIndex - 1 + totalImages) % totalImages];
  const nextImage=section.images[(activeIndex + 1) % totalImages];

  const goPrev=()=>setActiveIndex((current)=>(current - 1 + totalImages) % totalImages);
  const goNext=()=>setActiveIndex((current)=>(current + 1) % totalImages);

  return(
    <section className="gallery-section page-section" aria-labelledby={`${section.id}-title`}>
      <div className="gallery-section__header">
        <div>
          <h2 id={`${section.id}-title`}>{section.title}</h2>
          <p>{section.description}</p>
        </div>
        <span className="gallery-section__count">
          {totalImages} {totalImages === 1 ? 'Bild' : 'Bilder'}
        </span>
      </div>

      <div className="gallery-carousel">
        <div className="gallery-carousel__frame">
          {totalImages > 1 && (
            <div className="gallery-carousel__preview gallery-carousel__preview--prev" aria-hidden="true">
              <img src={prevImage.src} alt="" loading="lazy" />
            </div>
          )}

          <div className="gallery-carousel__main">
            <img src={activeImage.src} alt={activeImage.alt} loading="lazy" />
          </div>

          {totalImages > 1 && (
            <div className="gallery-carousel__preview gallery-carousel__preview--next" aria-hidden="true">
              <img src={nextImage.src} alt="" loading="lazy" />
            </div>
          )}

          {totalImages > 1 && (
            <>
              <button
                type="button"
                className="gallery-carousel__nav gallery-carousel__nav--prev"
                aria-label={`Vorheriges Bild aus ${section.title}`}
                onClick={goPrev}
              >
                ‹
              </button>
              <button
                type="button"
                className="gallery-carousel__nav gallery-carousel__nav--next"
                aria-label={`Nächstes Bild aus ${section.title}`}
                onClick={goNext}
              >
                ›
              </button>
            </>
          )}
        </div>

        {totalImages > 1 && (
          <div className="gallery-carousel__dots" aria-label={`Bildauswahl ${section.title}`}>
            {section.images.map((image, index)=>(
              <button
                type="button"
                key={image.id}
                className={`gallery-carousel__dot ${index === activeIndex ? 'is-active' : ''}`}
                aria-label={`${section.title}: Bild ${index + 1}`}
                aria-pressed={index === activeIndex}
                onClick={()=>setActiveIndex(index)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const galerieBreadcrumb=breadcrumbJsonLd([{name:'Startseite', path:'/'}, {name:'Galerie', path:'/galerie'}]);

const Galerie=()=>{
  const sections=gallerySections.filter((section)=>section.images.length > 0);
  usePageMeta({
    title:'Galerie | Cura Care Pflegedienst Eitorf',
    description:'Einblicke in unseren Alltag, Messen und Aktionen: die Bildergalerie von Cura Care in Eitorf.',
    path:'/galerie',
    jsonLd:galerieBreadcrumb
  });

  return(
    <div className="page-view">
      <Header />
      <main className="page galerie-page" id="galerie-page">
        <div className="page__inner">
          <nav className="breadcrumb" aria-label="Brotkrumen">
            <Link to="/">Startseite</Link>
            <span aria-hidden="true">/</span>
            <span>Galerie</span>
          </nav>
          <div className="page__header">
            <h1>Galerie</h1>
            <p>
              Einblicke in unseren Pflegealltag, besondere Aktionen und öffentliche Auftritte.
            </p>
          </div>

          {sections.map((section)=>(
            <GallerySection key={section.id} section={section} />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Galerie;
